import React, { useState, useRef, useEffect } from 'react';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  bg: '#F9F7F4',
  surface: '#FFFFFF',
  surfaceAlt: '#F2EFE9',
  border: '#E4DDD4',
  text: '#26211C',
  muted: '#9A8F85',
  accent: '#8B7355',
  accentLight: '#EDE6DA',
  accentDark: '#6B5840',
  sage: '#7A9180',
  sageLight: '#DCE9E2',
  gold: '#C5A55A',
  error: '#A84040',
};

const display = "'Cormorant Garamond', Georgia, serif";
const body = "'Jost', 'Helvetica Neue', sans-serif";
const FOUNDATION_PLAN_URL = 'https://buy.stripe.com/28EbJ32DU7qp7lH0VvgnK03';
const DECODER_PLAN_URL = 'https://buy.stripe.com/dRm4gBguK7qp8pLcEdgnK02';

const stripeUrl = (baseUrl, uid) => `${baseUrl}?client_reference_id=${encodeURIComponent(uid)}`;
const accountCreationPath = '/?mode=create';

// ─── Languages ───────────────────────────────────────────────────────────────
const LANGS = [
  { code:'en', native:'English',    flag:'🇺🇸' },
  { code:'es', native:'Español',    flag:'🇪🇸' },
  { code:'pt', native:'Português',  flag:'🇧🇷' },
  { code:'it', native:'Italiano',   flag:'🇮🇹' },
  { code:'ru', native:'Русский',    flag:'🇷🇺' },
  { code:'fr', native:'Français',   flag:'🇫🇷' },
  { code:'de', native:'Deutsch',    flag:'🇩🇪' },
  { code:'ar', native:'العربية',    flag:'🇸🇦' },
];

const T = {
  en: {
    tagline: 'Make sense of what you\'re feeling.',
    signIn: 'Sign In', create: 'Create Account',
    emailLabel: 'Email address', passLabel: 'Password',
    emailPh: 'you@example.com', passPh: '••••••••',
    noAccount: 'New here?', hasAccount: 'Already have an account?',
    guest: 'Continue as Guest',
    monthlyNote: <>Premium access · <span style={{textDecoration:'line-through', opacity:0.5}}>$12</span> $5/month — Launch offer</>,
    privacyBtn: '🔒 Privacy & Disclaimer',
    privacyTitle: 'Important information',
    privacyBody: 'This is a private self-reflection tool designed to help you make sense of what you\'re feeling and figure out what to do next.\n\nIt is not therapy or a substitute for professional care.\n\nInformation remains confidential — please exercise personal discretion.\n\nFor more information visit our website: www.transcendental.care',
    loading: 'Loading…',
    fieldRequired: 'Please fill in this field.',
    emailInvalid: 'Please enter a valid email.',
  },
  es: {
    tagline: 'Entiende lo que estás sintiendo.',
    signIn: 'Iniciar sesión', create: 'Crear cuenta',
    emailLabel: 'Correo electrónico', passLabel: 'Contraseña',
    emailPh: 'tu@ejemplo.com', passPh: '••••••••',
    noAccount: '¿Nuevo aquí?', hasAccount: '¿Ya tienes cuenta?',
    guest: 'Continuar como invitado',
    monthlyNote: <>Acceso premium · <span style={{textDecoration:'line-through', opacity:0.5}}>$12</span> $5/mes — Oferta de lanzamiento</>,
    privacyBtn: '🔒 Privacidad y aviso',
    privacyTitle: 'Información importante',
    privacyBody: 'Esta es una herramienta privada de autorreflexión diseñada para ayudarte a entender lo que sientes y decidir qué hacer a continuación.\n\nNo es terapia ni un sustituto de atención profesional.\n\nLa información es confidencial — ejerce discreción personal.\n\nMás info: www.transcendental.care',
    loading: 'Cargando…',
    fieldRequired: 'Por favor completa este campo.',
    emailInvalid: 'Ingresa un correo válido.',
  },
  pt: {
    tagline: 'Entenda o que você está sentindo.',
    signIn: 'Entrar', create: 'Criar conta',
    emailLabel: 'E-mail', passLabel: 'Senha',
    emailPh: 'voce@exemplo.com', passPh: '••••••••',
    noAccount: 'Novo por aqui?', hasAccount: 'Já tem uma conta?',
    guest: 'Continuar como convidado',
    monthlyNote: <>Acesso premium · <span style={{textDecoration:'line-through', opacity:0.5}}>$12</span> $5/mês — Oferta de lançamento</>,
    privacyBtn: '🔒 Privacidade e aviso',
    privacyTitle: 'Informação importante',
    privacyBody: 'Esta é uma ferramenta privada de autorreflexão para ajudá-lo a entender o que sente e decidir o que fazer a seguir.\n\nNão é terapia nem substitui cuidados profissionais.\n\nAs informações são confidenciais — use com discrição pessoal.\n\nMais informações: www.transcendental.care',
    loading: 'Carregando…',
    fieldRequired: 'Por favor preencha este campo.',
    emailInvalid: 'Insira um e-mail válido.',
  },
  it: {
    tagline: 'Capire cosa stai provando.',
    signIn: 'Accedi', create: 'Crea account',
    emailLabel: 'E-mail', passLabel: 'Password',
    emailPh: 'tu@esempio.com', passPh: '••••••••',
    noAccount: 'Nuovo qui?', hasAccount: 'Hai già un account?',
    guest: 'Continua come ospite',
    monthlyNote: <>Accesso premium · <span style={{textDecoration:'line-through', opacity:0.5}}>$12</span> $5/mese — Offerta di lancio</>,
    privacyBtn: '🔒 Privacy e avviso',
    privacyTitle: 'Informazioni importanti',
    privacyBody: 'Questo è uno strumento privato di auto-riflessione per aiutarti a capire cosa provi e cosa fare dopo.\n\nNon è terapia né un sostituto di cure professionali.\n\nLe informazioni sono riservate — usa con discrezione personale.\n\nPer ulteriori info: www.transcendental.care',
    loading: 'Caricamento…',
    fieldRequired: 'Per favore compila questo campo.',
    emailInvalid: 'Inserisci un\'e-mail valida.',
  },
  ru: {
    tagline: 'Разберись в том, что ты чувствуешь.',
    signIn: 'Войти', create: 'Создать аккаунт',
    emailLabel: 'Эл. почта', passLabel: 'Пароль',
    emailPh: 'ты@пример.ru', passPh: '••••••••',
    noAccount: 'Впервые здесь?', hasAccount: 'Уже есть аккаунт?',
    guest: 'Продолжить как гость',
    monthlyNote: <>Премиум доступ · <span style={{textDecoration:'line-through', opacity:0.5}}>$12</span> $5/мес — Акция запуска</>,
    privacyBtn: '🔒 Конфиденциальность',
    privacyTitle: 'Важная информация',
    privacyBody: 'Это приватный инструмент для самоанализа.\n\nЭто не терапия и не замена профессиональной помощи.\n\nИнформация конфиденциальна.\n\nПодробнее: www.transcendental.care',
    loading: 'Загрузка…',
    fieldRequired: 'Пожалуйста, заполните это поле.',
    emailInvalid: 'Введите действительный адрес эл. почты.',
  },
  fr: {
    tagline: 'Comprendre ce que vous ressentez.',
    signIn: 'Se connecter', create: 'Créer un compte',
    emailLabel: 'Adresse e-mail', passLabel: 'Mot de passe',
    emailPh: 'vous@exemple.fr', passPh: '••••••••',
    noAccount: 'Nouveau ici ?', hasAccount: 'Déjà un compte ?',
    guest: 'Continuer en tant qu\'invité',
    monthlyNote: <>Accès premium · <span style={{textDecoration:'line-through', opacity:0.5}}>$12</span> $5/mois — Offre de lancement</>,
    privacyBtn: '🔒 Confidentialité et avis',
    privacyTitle: 'Information importante',
    privacyBody: 'Ceci est un outil privé d\'auto-réflexion.\n\nCe n\'est pas de la thérapie ni un substitut à des soins professionnels.\n\nLes informations restent confidentielles.\n\nPlus d\'infos : www.transcendental.care',
    loading: 'Chargement…',
    fieldRequired: 'Veuillez remplir ce champ.',
    emailInvalid: 'Veuillez entrer un e-mail valide.',
  },
  de: {
    tagline: 'Verstehe was du fühlst.',
    signIn: 'Anmelden', create: 'Konto erstellen',
    emailLabel: 'E-Mail-Adresse', passLabel: 'Passwort',
    emailPh: 'du@beispiel.de', passPh: '••••••••',
    noAccount: 'Neu hier?', hasAccount: 'Hast du schon ein Konto?',
    guest: 'Als Gast fortfahren',
    monthlyNote: <>Premium · <span style={{textDecoration:'line-through', opacity:0.5}}>$12</span> $5/Monat — Startangebot</>,
    privacyBtn: '🔒 Datenschutz & Hinweis',
    privacyTitle: 'Wichtige Information',
    privacyBody: 'Dies ist ein privates Selbstreflexions-Tool.\n\nEs ist keine Therapie und kein Ersatz für professionelle Betreuung.\n\nDie Informationen bleiben vertraulich.\n\nMehr Infos: www.transcendental.care',
    loading: 'Laden…',
    fieldRequired: 'Bitte fülle dieses Feld aus.',
    emailInvalid: 'Bitte gib eine gültige E-Mail ein.',
  },
  ar: {
    tagline: 'افهم ما تشعر به.',
    signIn: 'تسجيل الدخول', create: 'إنشاء حساب',
    emailLabel: 'البريد الإلكتروني', passLabel: 'كلمة المرور',
    emailPh: 'أنت@مثال.com', passPh: '••••••••',
    noAccount: 'جديد هنا؟', hasAccount: 'هل لديك حساب بالفعل؟',
    guest: 'المتابعة كضيف',
    monthlyNote: <>وصول بريميوم · <span style={{textDecoration:'line-through', opacity:0.5}}>$12</span> $5/شهر — عرض الإطلاق</>,
    privacyBtn: '🔒 الخصوصية والإشعار',
    privacyTitle: 'معلومات مهمة',
    privacyBody: 'هذه أداة خاصة للتأمل الذاتي.\n\nهذه ليست علاجًا أو بديلاً عن الرعاية المهنية.\n\nتبقى المعلومات سرية.\n\nلمزيد من المعلومات: www.transcendental.care',
    loading: 'جارٍ التحميل…',
    fieldRequired: 'يرجى ملء هذا الحقل.',
    emailInvalid: 'يرجى إدخال بريد إلكتروني صحيح.',
  },
};

// ─── Inject fonts + reset ─────────────────────────────────────────────────────
const injectStyles = () => {
  if (document.getElementById('decoder-login-styles')) return;
  const link = document.createElement('link');
  link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&family=Jost:wght@300;400;500;600&display=swap';
  link.rel = 'stylesheet';
  document.head.appendChild(link);

  const style = document.createElement('style');
  style.id = 'decoder-login-styles';
  style.textContent = `
    @keyframes decoder-fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
    @keyframes decoder-spin { to { transform: rotate(360deg); } }
    .decoder-fade { animation: decoder-fadeUp 0.45s ease both; }
    .decoder-btn-hover:hover { filter: brightness(0.92); transform: translateY(-1px); transition: all 0.18s; }
    .decoder-btn-hover:active { transform: translateY(0); }
  `;
  document.head.appendChild(style);
};

// ─── Privacy Modal ─────────────────────────────────────────────────────────────
function PrivacyModal({ t, onClose }) {
  return (
    <div
      role="dialog" aria-modal="true"
      style={{ position:'fixed', inset:0, background:'rgba(38,33,28,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'20px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background:C.surface, borderRadius:'20px', padding:'32px', maxWidth:'440px', width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.2)', fontFamily:body }}>
        <h2 style={{ fontFamily:display, fontSize:'22px', fontWeight:600, marginBottom:'16px', color:C.text }}>{t.privacyTitle}</h2>
        <p style={{ fontSize:'14px', lineHeight:1.8, color:C.text, whiteSpace:'pre-line', margin:0 }}>{t.privacyBody}</p>
        <button
          onClick={onClose} autoFocus
          style={{ marginTop:'24px', width:'100%', padding:'13px', background:C.accent, color:'#fff', border:'none', borderRadius:'10px', fontFamily:body, fontSize:'14px', fontWeight:500, cursor:'pointer' }}
        >
          I understand ✓
        </button>
      </div>
    </div>
  );
}

const ABOUT_SECTIONS = [
  {
    title: 'ABOUT THE DECODER',
    body: `The Decoder is an AI-powered self-reflection tool designed to help you make sense of what you're feeling - in the moment, on your own terms.

Most of us carry questions we don't know how to ask. Emotions we can't quite name. Situations we keep finding ourselves in without understanding why. The Decoder gives you a space to bring those things forward - and helps you begin to understand them.

The philosophy is simple: clarity creates agency. When you understand what's happening inside you, you stop reacting and start choosing. That shift - from confusion to clarity - is where real change begins.

Every session moves through four stages: being Seen, being Understood, feeling Empowered, and acting with Intention. Not as a formula. As a natural process of self-discovery.`
  },
  {
    title: 'ABOUT THE FOUNDATION',
    body: `The Foundation is the psychoeducation library your therapist wishes you already had.

Most people spend years in therapy slowly learning concepts that could have transformed their experience from day one. The Foundation delivers that knowledge directly - through audio episodes and reading guides designed to be honest, practical, and deeply human.

Topics include anxiety, trauma responses, emotions, relationships, self-esteem, generational patterns, and more. Each series is built from real therapy sessions - anonymized, distilled, and made accessible to anyone ready to understand themselves better.

The Foundation is not a replacement for therapy. It is the foundation that makes therapy more effective - and makes self-growth possible for people who don't yet have access to it.

New episodes are added monthly, drawn directly from real clinical work.`
  },
  {
    title: 'ABOUT TRANSCENDENTAL CARE',
    body: `Transcendental Care is a digital wellness company built on one belief: that psychological education should be accessible to everyone - not just those who can afford weekly sessions.

We build tools that bridge the gap between professional mental health care and everyday self-awareness. Our products are designed to complement therapy, accelerate personal growth, and give people the language to understand their own inner world.

Mental health. Balance. Perspective.`
  },
  {
    title: 'ABOUT THE CREATOR',
    body: `The Decoder and The Foundation were created by a licensed clinical social worker (LCSW) with years of clinical experience working with individuals, couples, and families across cultures and languages.

This work was born from a simple frustration: watching clients spend months - sometimes years - in therapy learning concepts that could have been introduced on day one. The foundation of self-awareness shouldn't be locked behind a paywall or a waiting list.

Everything in this app comes from real sessions, real patterns, and real people who were brave enough to do the work. Their stories - anonymized and honored - live in every episode.`
  },
  {
    title: 'LEGAL DISCLAIMER',
    body: `The Decoder and The Foundation are educational and self-reflection tools. They are not a substitute for professional mental health treatment, therapy, psychiatry, or medical advice.

The content provided in this app is for informational and psychoeducational purposes only. It is not intended to diagnose, treat, cure, or prevent any mental health condition or medical issue.

If you are experiencing a mental health crisis, thoughts of self-harm, or any situation requiring immediate support, please contact a licensed mental health professional or call your local emergency services.

In the United States, you can reach the 988 Suicide and Crisis Lifeline by calling or texting 988.

Using this app does not create a therapist-client relationship. All content is general in nature and may not apply to your specific situation. Always seek the advice of a qualified mental health professional with any questions you may have regarding your mental health.

Transcendental Care LLC - All rights reserved.`
  },
];

function AboutModal({ onClose }) {
  return (
    <div
      role="dialog" aria-modal="true"
      style={{ position:'fixed', inset:0, background:'rgba(38,33,28,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1050, padding:'20px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background:C.bg, borderRadius:'20px', width:'100%', maxWidth:'760px', maxHeight:'88vh', border:`1px solid ${C.border}`, boxShadow:'0 20px 60px rgba(0,0,0,0.2)', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{display:'flex',justifyContent:'flex-end',padding:'12px 14px',borderBottom:`1px solid ${C.border}`,background:C.surface}}>
          <button onClick={onClose} style={{border:'none',background:'transparent',color:C.muted,fontFamily:body,fontSize:'13px',cursor:'pointer',padding:'6px 10px'}}>Close ✕</button>
        </div>
        <div style={{padding:'22px 24px 28px',overflowY:'auto',scrollBehavior:'smooth'}}>
          {ABOUT_SECTIONS.map(section => (
            <section key={section.title} style={{marginBottom:'22px'}}>
              <h3 style={{margin:'0 0 10px',fontFamily:display,fontSize:'28px',fontWeight:600,color:C.gold,lineHeight:1.05}}>{section.title}</h3>
              <p style={{margin:0,whiteSpace:'pre-line',fontFamily:body,fontSize:'14px',lineHeight:1.85,color:C.muted}}>{section.body}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Language Picker ──────────────────────────────────────────────────────────
function LangPicker({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGS.find(l => l.code === lang) || LANGS[0];

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position:'relative', display:'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox" aria-expanded={open}
        style={{ background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:'10px', padding:'7px 12px', fontSize:'14px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', fontFamily:body, color:C.text }}
      >
        <span>{current.flag}</span>
        <span style={{ fontSize:'12px', color:C.muted }}>{current.native}</span>
        <span style={{ fontSize:'10px', color:C.muted }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div
          role="listbox"
          style={{ position:'absolute', top:'calc(100% + 6px)', right:0, background:C.surface, border:`1px solid ${C.border}`, borderRadius:'12px', boxShadow:'0 8px 24px rgba(0,0,0,0.12)', zIndex:100, minWidth:'180px', overflow:'hidden' }}
        >
          {LANGS.map(l => (
            <button
              key={l.code} role="option" aria-selected={lang === l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              style={{ display:'flex', alignItems:'center', gap:'10px', width:'100%', padding:'10px 14px', background:lang === l.code ? C.accentLight : 'transparent', border:'none', cursor:'pointer', fontFamily:body, fontSize:'13px', color:C.text, textAlign:'left' }}
            >
              <span>{l.flag}</span>
              <span>{l.native}</span>
              {lang === l.code && <span style={{ marginLeft:'auto', color:C.accent }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Login Component ─────────────────────────────────────────────────────
export default function Login({ onLoginSuccess, setNewUser }) {
  useEffect(() => { injectStyles(); }, []);

  const [lang, setLang] = useState('en');
  const [mode, setMode] = useState('signin'); // signin | create
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [focused, setFocused] = useState(null);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [accountCreated, setAccountCreated] = useState(false);
  const [premiumPrompt, setPremiumPrompt] = useState(false);

  const t = T[lang] || T.en;
  const isRTL = lang === 'ar';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const requestedMode = new URLSearchParams(window.location.search).get('mode');
    if (requestedMode === 'create') {
      setMode('create');
    }
  }, []);

  const validate = () => {
    const e = {};
    if (!email.trim()) e.email = t.fieldRequired;
    else if (!email.includes('@')) e.email = t.emailInvalid;
    if (!password.trim()) e.password = t.fieldRequired;
    return e;
  };

  const handleResetPassword = async () => {
    const targetEmail = (resetEmail || email).trim();
    if (!targetEmail) {
      setResetMessage('Please enter your email.');
      return;
    }
    if (!targetEmail.includes('@')) {
      setResetMessage('Please enter a valid email.');
      return;
    }

    setResetLoading(true);
    setResetMessage('');
    try {
      await sendPasswordResetEmail(auth, targetEmail);
      setResetMessage('Password reset email sent! Check your inbox.');
      setResetEmail('');
    } catch (err) {
      const code = err?.code;
      if (code === 'auth/user-not-found') {
        setResetMessage('No account found with that email.');
      } else {
        setResetMessage(err.message || 'Unable to send reset email.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  // ── Firebase Sign In (kept exactly from Lorka's build) ──
  const handleSignIn = async () => {
    const e = validate();
    if (Object.keys(e).length) { setFieldErrors(e); return; }
    setLoading(true); setError('');
    setPremiumPrompt(false);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const subRef = doc(db, 'subscribers', cred.user.uid);
      const subSnap = await getDoc(subRef);
      const subData = subSnap.exists() ? subSnap.data() : null;

      if (!subSnap.exists() || subData?.active !== true) {
        setPremiumPrompt(true);
      } else if (typeof onLoginSuccess === 'function') {
        onLoginSuccess(cred.user);
      }
    } catch (err) {
      setError(err.message || 'Unable to sign in.');
    } finally { setLoading(false); }
  };

  // ── Stripe Checkout create account flow ──
  const handleCreateAccount = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setFieldErrors(e);
      return;
    }

    setLoading(true);
    setError('');
    setAccountCreated(false);
    setPremiumPrompt(false);
    try {
      sessionStorage.setItem('justCreatedAccount', 'true');
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (cred?.user) {
        await setDoc(doc(db, 'subscribers', cred.user.uid), {
          active: false,
          email: cred.user.email,
          createdAt: new Date(),
        });
        setNewUser(true);
        setAccountCreated(true);
      }
    } catch (err) {
      const code = err?.code;
      if (code === 'auth/email-already-in-use') {
        setError('You already have an account. Please sign in instead.');
      } else {
        setError(err.message || 'Unable to create account.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleActivatePremium = (baseUrl, uid) => {
    setNewUser(false);
    if (!uid) {
      window.location.href = accountCreationPath;
      return;
    }
    window.location.href = stripeUrl(baseUrl, uid);
  };

  // ── Guest (kept from Lorka's build) ──
  const handleGuest = () => {
    if (typeof onLoginSuccess === 'function') onLoginSuccess({ guest: true });
  };

  const handleSubmit = () => mode === 'signin' ? handleSignIn() : handleCreateAccount();

  return (
    <>
      {showPrivacy && <PrivacyModal t={t} onClose={() => setShowPrivacy(false)} />}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      <div style={{ minHeight:'100vh', background:C.bg, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-start', padding:'32px 16px 48px', fontFamily:body }}>

        {/* Language picker top right */}
        <div style={{ width:'100%', maxWidth:'480px', display:'flex', justifyContent:'flex-end', marginBottom:'12px' }}>
          <LangPicker lang={lang} setLang={setLang} />
        </div>

        {/* Card */}
        <div className="decoder-fade" style={{ background:C.surface, borderRadius:'24px', padding:'36px 32px', width:'100%', maxWidth:'480px', boxShadow:'0 8px 48px rgba(44,33,28,0.09), 0 1px 3px rgba(44,33,28,0.06)', border:`1px solid ${C.border}` }}>

          {/* Logo */}
          <div style={{ textAlign:'center', marginBottom:'28px' }}>
            <div style={{ fontSize:'13px', fontFamily:body, fontWeight:600, letterSpacing:'0.18em', color:C.gold, textTransform:'uppercase', marginBottom:'4px' }}>
              ✦ Premium ✦
            </div>
            <div style={{ fontSize:'38px', fontFamily:display, fontWeight:300, color:C.text, lineHeight:1, letterSpacing:'-0.01em' }}>
              The Decoder
            </div>
            <div style={{ fontSize:'14px', fontFamily:body, color:C.muted, marginTop:'6px', fontWeight:300 }}>
              {t.tagline}
            </div>
          </div>

          {/* Premium note on create */}
          {mode === 'create' && (
            <div style={{ background:C.sageLight, borderRadius:'10px', padding:'12px 14px', marginBottom:'20px', fontSize:'12px', fontFamily:body, color:C.sage, display:'flex', gap:'8px', alignItems:'flex-start' }}>
              <span>✦</span>
              <span>{t.monthlyNote}</span>
            </div>
          )}

          {/* Sign in / Create toggle */}
          <div style={{ display:'flex', borderRadius:'12px', border:`1px solid ${C.border}`, overflow:'hidden', marginBottom:'24px' }}>
            {['signin','create'].map(m => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(''); setFieldErrors({}); setAccountCreated(false); }}
                style={{ flex:1, padding:'11px', fontFamily:body, fontSize:'13px', fontWeight:500, background:mode===m ? C.accent : 'transparent', color:mode===m ? '#fff' : C.muted, border:'none', cursor:'pointer', transition:'all 0.2s' }}
              >
                {m === 'signin' ? t.signIn : t.create}
              </button>
            ))}
          </div>

          {accountCreated && mode === 'create' && (
            <div style={{ background:'#DCE9E2', borderRadius:'12px', padding:'16px', marginBottom:'16px', textAlign:'center' }}>
              <p style={{ fontFamily:"'Jost', sans-serif", fontSize:'14px', color:'#7A9180', marginBottom:'12px', fontWeight:500 }}>
                Your account is ready. Choose your plan to get started:
              </p>
              <button
                onClick={() => handleActivatePremium(FOUNDATION_PLAN_URL, auth.currentUser?.uid)}
                style={{ width:'100%', padding:'13px', borderRadius:'10px', border:'none', background:'#8B7355', color:'#fff', fontSize:'14px', fontFamily:"'Jost',sans-serif", fontWeight:500, cursor:'pointer', marginBottom:'8px' }}
              >
                ✦ Decoder + The Foundation — $15/month
                <span style={{ display:'block', fontSize:'11px', opacity:0.8, marginTop:'2px' }}>Recommended · App + full course library</span>
              </button>
              <button
                onClick={() => handleActivatePremium(DECODER_PLAN_URL, auth.currentUser?.uid)}
                style={{ width:'100%', padding:'11px', borderRadius:'10px', border:'1.5px solid #8B7355', background:'transparent', color:'#8B7355', fontSize:'13px', fontFamily:"'Jost',sans-serif", cursor:'pointer' }}
              >
                Decoder only — $5/month
              </button>
              <p style={{ fontFamily:"'Jost', sans-serif", fontSize:'11px', color:C.muted, margin:'10px 0 0', lineHeight:1.5 }}>
                Cancel anytime · Secure payment via Stripe
              </p>
            </div>
          )}

          {premiumPrompt && mode === 'signin' && (
            <div style={{ background:'#F8EFE6', borderRadius:'12px', padding:'16px', marginBottom:'16px', textAlign:'center' }}>
              <p style={{fontFamily:"'Jost',sans-serif",fontSize:'14px',color:'#7A9180',marginBottom:'12px',fontWeight:500}}>
                Your account is ready. Choose your plan to get started:
              </p>
              <button
                onClick={() => handleActivatePremium(FOUNDATION_PLAN_URL, auth.currentUser?.uid)}
                style={{width:'100%',padding:'13px',borderRadius:'10px',border:'none',background:'#8B7355',color:'#fff',fontSize:'14px',fontFamily:"'Jost',sans-serif",fontWeight:500,cursor:'pointer',marginBottom:'8px'}}>
                ✦ Decoder + The Foundation — $15/month
                <span style={{display:'block',fontSize:'11px',opacity:0.8,marginTop:'2px'}}>Recommended · App + full course library</span>
              </button>
              <button
                onClick={() => handleActivatePremium(DECODER_PLAN_URL, auth.currentUser?.uid)}
                style={{width:'100%',padding:'11px',borderRadius:'10px',border:'1.5px solid #8B7355',background:'transparent',color:'#8B7355',fontSize:'13px',fontFamily:"'Jost',sans-serif",cursor:'pointer',marginBottom:'8px'}}>
                Decoder only — $5/month
              </button>
              <p style={{fontSize:'11px',color:'#9A8F85',textAlign:'center'}}>Cancel anytime · Secure payment via Stripe</p>
            </div>
          )}

          {/* Email */}
          <div style={{ marginBottom:'14px' }}>
            <label htmlFor="decoder-email" style={{ display:'block', fontSize:'11px', fontFamily:body, fontWeight:600, letterSpacing:'0.07em', textTransform:'uppercase', color:C.muted, marginBottom:'6px' }}>
              {t.emailLabel}
            </label>
            <input
              id="decoder-email" type="email" autoComplete="email"
              placeholder={t.emailPh} value={email} dir={isRTL ? 'rtl' : 'ltr'}
              onChange={e => { setEmail(e.target.value); setFieldErrors(p => ({...p, email:undefined})); setError(''); }}
              onFocus={() => setFocused('email')} onBlur={() => setFocused(null)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={{ width:'100%', padding:'13px 15px', borderRadius:'10px', border:`1.5px solid ${fieldErrors.email ? C.error : focused==='email' ? C.accent : C.border}`, fontFamily:body, fontSize:'14px', background:C.surfaceAlt, color:C.text, boxSizing:'border-box' }}
              aria-invalid={!!fieldErrors.email}
            />
            {fieldErrors.email && <p role="alert" style={{ color:C.error, fontSize:'11px', fontFamily:body, marginTop:'4px', margin:'4px 0 0' }}>{fieldErrors.email}</p>}
          </div>

          {/* Password */}
          <div style={{ marginBottom:'20px' }}>
            <label htmlFor="decoder-password" style={{ display:'block', fontSize:'11px', fontFamily:body, fontWeight:600, letterSpacing:'0.07em', textTransform:'uppercase', color:C.muted, marginBottom:'6px' }}>
              {t.passLabel}
            </label>
            <input
              id="decoder-password" type="password"
              autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
              placeholder={t.passPh} value={password}
              onChange={e => { setPassword(e.target.value); setFieldErrors(p => ({...p, password:undefined})); setError(''); }}
              onFocus={() => setFocused('password')} onBlur={() => setFocused(null)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={{ width:'100%', padding:'13px 15px', borderRadius:'10px', border:`1.5px solid ${fieldErrors.password ? C.error : focused==='password' ? C.accent : C.border}`, fontFamily:body, fontSize:'14px', background:C.surfaceAlt, color:C.text, boxSizing:'border-box' }}
              aria-invalid={!!fieldErrors.password}
            />
            {fieldErrors.password && <p role="alert" style={{ color:C.error, fontSize:'11px', fontFamily:body, margin:'4px 0 0' }}>{fieldErrors.password}</p>}

            {mode === 'signin' && (
              <div style={{ marginTop:'8px', display:'flex', justifyContent:'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setShowReset(v => !v); setResetMessage(''); }}
                  style={{ background:'transparent', border:'none', padding:0, color:C.muted, fontSize:'12px', fontFamily:body, cursor:'pointer', textDecoration:'underline', textDecorationColor:'rgba(155,143,133,0.4)' }}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {mode === 'signin' && showReset && (
              <div style={{ marginTop:'10px', padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:'10px', background:C.surfaceAlt }}>
                <label htmlFor="decoder-reset-email" style={{ display:'block', fontSize:'10px', fontFamily:body, fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', color:C.muted, marginBottom:'6px' }}>
                  Email address
                </label>
                <div style={{ display:'flex', gap:'8px' }}>
                  <input
                    id="decoder-reset-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={resetEmail}
                    onChange={e => { setResetEmail(e.target.value); setResetMessage(''); }}
                    style={{ flex:1, padding:'10px 12px', borderRadius:'8px', border:`1px solid ${C.border}`, fontFamily:body, fontSize:'13px', background:'#fff', color:C.text, boxSizing:'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={resetLoading}
                    style={{ padding:'10px 12px', borderRadius:'8px', border:'none', background:resetLoading ? C.muted : C.accent, color:'#fff', fontFamily:body, fontSize:'13px', cursor:resetLoading ? 'wait' : 'pointer' }}
                  >
                    {resetLoading ? 'Sending…' : 'Send'}
                  </button>
                </div>
                {resetMessage && (
                  <p style={{ margin:'8px 0 0', fontSize:'12px', fontFamily:body, color:resetMessage.includes('sent') ? C.sage : C.error }}>
                    {resetMessage}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Firebase error */}
          {error && (
            <div role="alert" style={{ background:'#FDF0F0', border:`1px solid ${C.error}`, borderRadius:'8px', padding:'10px 14px', marginBottom:'14px', fontSize:'13px', color:C.error, fontFamily:body }}>
              {error}
            </div>
          )}

          {/* Primary action */}
          {!accountCreated && (
            <button
              className="decoder-btn-hover"
              onClick={handleSubmit} disabled={loading}
              style={{ width:'100%', padding:'14px', borderRadius:'12px', border:'none', background:loading ? C.muted : C.accent, color:'#fff', fontSize:'15px', fontFamily:body, fontWeight:500, cursor:loading ? 'wait' : 'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}
            >
              {loading ? (
                <><div style={{ width:'16px', height:'16px', border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'#fff', borderRadius:'50%', animation:'decoder-spin 0.8s linear infinite' }} />{t.loading}</>
              ) : (
                mode === 'signin' ? t.signIn : t.create
              )}
            </button>
          )}

          {mode === 'create' && (
            <p style={{ textAlign:'center', fontFamily:body, fontSize:'12px', color:C.muted, marginTop:'10px', marginBottom:'0' }}>
              Already paid? Sign in with your email above.
            </p>
          )}

          {/* Guest */}
          <button
            className="decoder-btn-hover"
            onClick={handleGuest}
            style={{ width:'100%', marginTop:'10px', padding:'13px', borderRadius:'12px', border:`1.5px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:'13px', fontFamily:body, fontWeight:400, cursor:'pointer' }}
          >
            {t.guest}
          </button>

          {/* Switch mode */}
          <p style={{ textAlign:'center', fontFamily:body, fontSize:'12px', color:C.muted, marginTop:'14px', margin:'14px 0 0' }}>
            {mode === 'signin' ? t.noAccount : t.hasAccount}{' '}
            <span
              onClick={() => { setMode(mode === 'signin' ? 'create' : 'signin'); setError(''); setFieldErrors({}); }}
              style={{ color:C.accent, cursor:'pointer', fontWeight:500 }}
            >
              {mode === 'signin' ? t.create : t.signIn}
            </span>
          </p>

          {/* Divider */}
          <div style={{ height:'1px', background:C.border, margin:'20px 0' }} />

          {/* Privacy button */}
          <button
            onClick={() => setShowPrivacy(true)}
            style={{ width:'100%', background:'none', border:`1px solid ${C.border}`, borderRadius:'10px', padding:'11px', fontFamily:body, fontSize:'12px', color:C.muted, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'7px' }}
          >
            {t.privacyBtn}
          </button>

          {/* Disclaimer */}
          <p style={{ fontSize:'11px', color:C.muted, textAlign:'center', marginTop:'16px', fontFamily:body, lineHeight:1.5, margin:'16px 0 0' }}>
            This tool offers supportive guidance, not clinical therapy. In crisis? Call or text 988.
          </p>

          <button
            onClick={() => setShowAbout(true)}
            style={{marginTop:'10px',width:'100%',background:'none',border:'none',padding:'4px 0',fontFamily:body,fontSize:'12px',color:C.muted,cursor:'pointer'}}
          >
            About
          </button>
        </div>
      </div>
    </>
  );
}
